// Follow-up modal -- appends a dated note to an existing Activity Log card
// AND lets the rep advance the entry's status (In the Hunt -> Bidding -> Won,
// or Lost / Closed). Doesn't create a new entry.
//
// Extracted from CallLog.jsx (2026-07-07) so the Dashboard's Follow-ups Due
// list can log a follow-up in place -- the AM shouldn't have to leave the
// dashboard and hunt down the entry. The activity-pipeline constants live
// here now (single source); CallLog imports them back.
//
// Follow-up date is REQUIRED when the (final) status is still open. If the
// rep is marking the entry Closed or Lost here, the next-date field is
// optional -- nothing to chase. Server enforces the same rule.
import { useState } from 'react';
import { styles } from './UI';

// Activity Log statuses (5):
//   In the Hunt -- aware, prospecting toward a bid
//   Bidding     -- actively quoting (formerly "Active")
//   Won / Lost  -- closed terminal
//   Closed      -- no opportunity, customer not buying (terminal, no follow-up needed)
export const STATUSES = ['In the Hunt', 'Bidding', 'Won', 'Lost', 'Closed'];

// Statuses that REQUIRE a follow-up date. Lost + Closed are terminal --
// nothing to chase, so the form lets you save without one.
export const STATUSES_NEEDING_FOLLOWUP = new Set(['In the Hunt', 'Bidding', 'Won']);

// Standard contact-type options for the dropdown. Tagging an activity with
// "what kind of touch was this" helps roll up activity reports later.
// Cold Call exempts the rep from filling Contact Name + Title -- a walk-in
// where they didn't get to a named person should still be loggable.
export const CONTACT_TYPES = [
  { value: 'Cold Call',    label: '🚪 Cold Call / Walk-in' },
  { value: 'Phone Call',   label: '📞 Phone Call' },
  { value: 'Meeting',      label: '🍽️ Meeting (in person)' },
  { value: 'Email',        label: '📧 Email' },
  { value: 'Text Message', label: '💬 Text Message' },
  { value: 'Site Visit',   label: '🚗 Site Visit' },
  { value: 'Video Call',   label: '💻 Video Call' },
  { value: 'Other',        label: 'Other' }
];

const Field = ({ label, children }) => (
  <div>
    <label style={styles.label}>{label}</label>
    {children}
  </div>
);

export default function FollowUpModal({ entry, onCancel, onSave }) {
  const today = new Date().toISOString().slice(0, 10);
  const [occurredOn, setOccurredOn] = useState(today);
  const [contactType, setContactType] = useState('');
  const [notes, setNotes] = useState('');
  const [nextDate, setNextDate] = useState('');
  const [nextType, setNextType] = useState('');
  // Status update -- defaults to "no change", drops in the entry's current
  // status as the leading option for context.
  const [newStatus, setNewStatus] = useState('');
  const [busy, setBusy] = useState(false);

  // Effective status after this save: explicit pick, else current.
  const effectiveStatus = newStatus || entry.status;
  const needsFollowupDate = STATUSES_NEEDING_FOLLOWUP.has(effectiveStatus);

  const canSubmit = contactType && notes.trim() && (!needsFollowupDate || nextDate);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!canSubmit) return;
    setBusy(true);
    try {
      await onSave({
        occurred_on: occurredOn,
        contact_type: contactType,
        notes: notes.trim(),
        next_follow_up_date: nextDate || null,
        next_follow_up_type: nextType || null,
        new_status: newStatus || null
      });
    } finally { setBusy(false); }
  };

  return (
    <div
      onClick={onCancel}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50
      }}
    >
      <form
        onSubmit={submit}
        onClick={e => e.stopPropagation()}
        style={{
          background: '#fff', borderRadius: 8, padding: 20,
          // width, NOT minWidth: min-width beats max-width in CSS, so
          // minWidth 480 overflowed a 375px phone despite the 100vw clamp.
          width: 480, maxWidth: 'calc(100vw - 32px)', maxHeight: 'calc(100vh - 64px)', overflow: 'auto',
          boxShadow: '0 12px 40px rgba(0,0,0,0.25)'
        }}
      >
        <h2 style={{ ...styles.h2, marginTop: 0 }}>+ Follow-up · {entry.customer}</h2>
        <div style={{ fontSize: 12, color: '#6B7280', marginBottom: 12 }}>
          {entry.contact_name && <>Contact on file: <strong>{entry.contact_name}</strong>{entry.contact_title ? `, ${entry.contact_title}` : ''} · </>}
          Current status: <strong>{entry.status}</strong> · appends a dated note, doesn't create a new entry.
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <Field label="Date *">
            <input type="date" style={styles.input} value={occurredOn} onChange={e => setOccurredOn(e.target.value)} />
          </Field>
          <Field label="Contact Type *">
            <select style={styles.input} value={contactType} onChange={e => setContactType(e.target.value)}>
              <option value="">— pick one —</option>
              {CONTACT_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </Field>
        </div>

        <Field label="What happened *">
          <textarea
            style={{ ...styles.textarea, minHeight: 100 }}
            placeholder="e.g. Met onsite. Walked the equipment list. Quote going out Friday."
            value={notes}
            onChange={e => setNotes(e.target.value)}
          />
        </Field>

        <div style={{ borderTop: '1px solid #E5E7EB', marginTop: 8, paddingTop: 12 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <Field label="Update Status">
              <select style={styles.input} value={newStatus} onChange={e => setNewStatus(e.target.value)}>
                <option value="">— Keep "{entry.status}" —</option>
                {STATUSES.filter(s => s !== entry.status).map(s => (
                  <option key={s} value={s}>Move to {s}</option>
                ))}
              </select>
              <div style={{ fontSize: 11, color: '#6B7280', marginTop: 4 }}>
                {needsFollowupDate
                  ? 'Open status — next follow-up date is required.'
                  : 'Terminal status — no follow-up date needed.'}
              </div>
            </Field>
            <Field label={needsFollowupDate ? 'Next Follow-up Date *' : 'Next Follow-up Date'}>
              <input type="date" style={styles.input} value={nextDate} onChange={e => setNextDate(e.target.value)} />
            </Field>
          </div>
          <Field label="Next Follow-up Type">
            <input style={styles.input} placeholder="Lunch, Golf, Call..." value={nextType} onChange={e => setNextType(e.target.value)} />
          </Field>
        </div>

        <div style={{ display: 'flex', gap: 8, marginTop: 16, justifyContent: 'flex-end' }}>
          <button type="button" style={styles.btnSecondary} onClick={onCancel} disabled={busy}>Cancel</button>
          <button type="submit" style={{ ...styles.btn, opacity: canSubmit ? 1 : 0.5 }} disabled={busy || !canSubmit}>
            {busy ? 'Saving…' : 'Save Follow-up'}
          </button>
        </div>
      </form>
    </div>
  );
}
